import { useCallback, useState } from 'react'
import { db } from '@/services/index.ts'

/**
 * Versioned label for the published Terms & Conditions document.
 * Bump this whenever the document at TERMS_URL is materially updated, so
 * audit trails can disambiguate which text the user agreed to.
 */
export const TERMS_VERSION = '2026-05-01'
export const TERMS_URL = 'https://www.bitslegacy.com/terms.html'

/** UI surface that captured the consent. */
export type TermsConsentSource =
  | 'hero_benefactor'
  | 'hero_beneficiary'
  | 'pricing_get_started'

interface TermsConsentRow {
  id: string
  wallet_address: string | null
  terms_version: string
  terms_url: string
  source: string | null
  language: string | null
  accepted_at: string
  user_agent: string | null
  created_at: string
}

export interface RecordConsentParams {
  source: TermsConsentSource
  walletAddress?: string | null
  language?: string
}

interface UseTermsConsentReturn {
  loading: boolean
  error: string | null
  record: (params: RecordConsentParams) => Promise<TermsConsentRow | null>
}

/**
 * Records the user's acceptance of the Terms & Conditions in Supabase.
 *
 * Insert is fire-and-forget from the caller's perspective: failures are
 * surfaced as a non-null `error` but never thrown, so the caller can still
 * progress with the original action (wallet connect / navigation) even if
 * the consent log temporarily fails (e.g. transient network).
 */
export function useTermsConsent(): UseTermsConsentReturn {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const record = useCallback(
    async ({
      source,
      walletAddress,
      language,
    }: RecordConsentParams): Promise<TermsConsentRow | null> => {
      setLoading(true)
      setError(null)
      try {
        const acceptedAt = new Date().toISOString()
        const userAgent =
          typeof navigator !== 'undefined' ? navigator.userAgent : null

        const { data, error: dbError } = await db.insert<TermsConsentRow>(
          'terms_consents',
          {
            wallet_address: walletAddress ? walletAddress.toLowerCase() : null,
            terms_version: TERMS_VERSION,
            terms_url: TERMS_URL,
            source,
            language: language ?? null,
            accepted_at: acceptedAt,
            // Truncate UA defensively; some browsers ship absurdly long values.
            user_agent: userAgent ? userAgent.slice(0, 1024) : null,
          },
        )

        if (dbError) {
          setError(dbError.message)
          return null
        }
        return data?.[0] ?? null
      } catch (e) {
        setError(
          e instanceof Error ? e.message : 'Failed to record terms consent',
        )
        return null
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  return { loading, error, record }
}
